import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface InterestInfoProps {
  formData: any;
  updateFormData: (field: string, value: string) => void;
}

interface HousingType {
  id: string;
  name: string;
  display_order: number;
}

export default function InterestInfo({ formData, updateFormData }: InterestInfoProps) {
  const [housingTypes, setHousingTypes] = useState<HousingType[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHousingTypes();
    
    // Listen for clear housing types event
    const handleClearEvent = () => {
      setSelectedTypes([]);
      updateFormData('housingType', '[]');
    };
    
    window.addEventListener('clearHousingTypes', handleClearEvent);
    
    return () => {
      window.removeEventListener('clearHousingTypes', handleClearEvent);
    };
  }, []);

  useEffect(() => {
    // formData'dan seçili tipleri yükle
    if (formData.housingType) {
      try {
        const parsed = JSON.parse(formData.housingType);
        if (Array.isArray(parsed)) {
          setSelectedTypes(parsed);
        }
      } catch {
        // Eski format string ise
        setSelectedTypes([]);
      }
    }
  }, []);

  const fetchHousingTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('housing_types')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setHousingTypes(data || []);
    } catch (error) {
      console.error('Konut tipleri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeToggle = (typeName: string) => {
    let newSelected: string[];
    if (selectedTypes.includes(typeName)) {
      newSelected = selectedTypes.filter(t => t !== typeName);
    } else {
      newSelected = [...selectedTypes, typeName];
    }
    setSelectedTypes(newSelected);
    updateFormData('housingType', JSON.stringify(newSelected));
  };

  return (
    <section className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
          <i className="ri-building-line text-violet-600 text-xl"></i>
        </div>
        <h2 className="text-xl font-semibold text-slate-800">İlgi Alanları</h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-3">
          İlgilendiğiniz Plan Tipleri <span className="text-red-500">*</span>
        </label>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <i className="ri-loader-4-line text-2xl text-violet-600 animate-spin"></i>
          </div>
        ) : housingTypes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Henüz konut tipi eklenmemiş
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {housingTypes.map((type) => (
              <label
                key={type.id}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedTypes.includes(type.name)
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-slate-200 hover:border-violet-300'
                }`}
              >
                <input
                  type="checkbox"
                  name="housingType"
                  checked={selectedTypes.includes(type.name)}
                  onChange={() => handleTypeToggle(type.name)}
                  className="w-5 h-5 text-violet-600 rounded focus:ring-2 focus:ring-violet-500 cursor-pointer"
                />
                <span className="text-sm font-medium text-slate-700">
                  {type.name}
                </span>
              </label>
            ))}
          </div>
        )}

        {selectedTypes.length > 0 && (
          <div className="mt-4 p-3 bg-violet-50 rounded-lg">
            <p className="text-xs text-violet-700 font-medium mb-2">
              Seçilen Konut Tipleri ({selectedTypes.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedTypes.map((type, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-xs font-medium"
                >
                  {type}
                  <button
                    type="button"
                    onClick={() => handleTypeToggle(type)}
                    className="w-4 h-4 flex items-center justify-center hover:bg-violet-200 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
